import React, { useEffect } from "react";
import { useSeo } from "../useSeo";
import { VENUE, PIX, CONTACT_EMAIL, CONTACT_PHONE, CHANNELS } from "../data";

// Handle do WhatsApp do Barnabe, lido do mesmo array que a pagina de canais
// usa (CHANNELS.igreja) -- evita duplicar o numero aqui e deixar desatualizado
// se ele mudar em data.js.
const WHATSAPP_HANDLE = CHANNELS.igreja.find((c) => c.icon === "whatsapp")?.handle || "";

// Perguntas e respostas visíveis na página — precisam bater exatamente com o
// bloco FAQPage injetado logo abaixo (useFaqSchema), senão a estrutura de
// dados fica inconsistente com o conteúdo real (viola as diretrizes do
// Google pra dados estruturados).
const FAQ_ITEMS = [
  {
    question: "Que horas são os cultos aos domingos?",
    answer: "Nosso culto é aos domingos: às 9h tem café da manhã (é só chegar e ficar à vontade) e às 10h começa a celebração, que termina por volta de 11h30."
  },
  {
    question: "Preciso me vestir de um jeito específico?",
    answer: "Não. Não temos dress code — vem como você é."
  },
  {
    question: "As crianças e os adolescentes ficam com os pais durante o culto?",
    answer: "A equipe Kids & Teens cuida e ensina as crianças e os adolescentes enquanto os pais estão na celebração."
  },
  {
    question: "Qual o endereço da igreja e como chegar?",
    answer: `Ficamos na ${VENUE.address}. O jeito mais fácil é seguir a rota pelo Google Maps.`
  },
  {
    question: "Tem estacionamento perto da igreja?",
    answer: "Sim! Temos estacionamento próprio, e aos domingos, se lotar, o bolsão da prefeitura ao lado também é liberado para quem for à igreja."
  },
  {
    question: "Como faço para contribuir com dízimos e ofertas?",
    answer: `Pelo Pix, com a chave ${PIX.key} (destinos: ${PIX.destinations.join(" · ")}) — o QR code e o código copia-e-cola estão na página de Contato.`
  },
  {
    question: "Como entro em contato com a igreja?",
    answer: `O jeito mais rápido é falar com o Barnabé, nosso assistente virtual, pelo WhatsApp (${WHATSAPP_HANDLE}) ou direto aqui no site. Também dá pra usar o formulário de contato, o telefone ${CONTACT_PHONE} ou o e-mail ${CONTACT_EMAIL}.`
  },
  {
    question: "Quem é o Barnabé?",
    answer: `É o nosso assistente virtual — responde pelo ícone de conversa aqui no site e também pelo WhatsApp (${WHATSAPP_HANDLE}), a qualquer hora. Ele tira dúvidas, ajuda a encontrar conteúdos da igreja e, quando é algo que precisa de uma pessoa de verdade (um pedido de oração, uma conversa pastoral), registra e encaminha pra equipe.`
  },
  {
    question: "Onde posso assistir às pregações e outros conteúdos?",
    answer: `No nosso canal do YouTube (${CHANNELS.igreja[0].handle}) e no Instagram (${CHANNELS.igreja[1].handle}), ou na página de Vídeos aqui do site, separados por Pregações, Podcast e Escola Bíblica.`
  },
  {
    question: "O que é o app Família Mosaico?",
    answer: "É o espaço para quem já faz parte da comunidade: trilhas devocionais, agenda de eventos, pedidos de oração e acompanhamento do seu crescimento espiritual. É só clicar em \"Acessar Família Mosaico\", no topo do site."
  }
];

// Injeta o schema FAQPage (JSON-LD) só enquanto esta página está montada —
// remove ao sair pra não vazar pra outras rotas da SPA. Mesma limitação de
// SSR documentada em useSeo.js: cobre o que rastreadores veem depois do JS
// carregar; o <head> do primeiro request não inclui esse script (só o Church
// do public/index.html), então isso é um reforço, não o SSR completo.
function useFaqSchema(items) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer
        }
      }))
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [items]);
}

export default function Faq() {
  useSeo({
    title: "Perguntas Frequentes | Igreja Batista Mosaico",
    description: "Horário dos cultos, o que vestir, atividades para crianças e adolescentes, endereço, como contribuir e como falar com a Igreja Batista Mosaico em Osasco.",
    path: "/perguntas-frequentes"
  });
  useFaqSchema(FAQ_ITEMS);

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Ainda com dúvidas?</p>
        <h1>Perguntas frequentes</h1>
        <p>As respostas mais pedidas por quem está chegando ou pensando em visitar.</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 36 }}>
        {FAQ_ITEMS.map((item) => (
          <div className="card" key={item.question}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </div>
        ))}
      </div>

      <div className="card venue-card venue-card--compact">
        <img className="venue-photo venue-photo--map" src={VENUE.photoContato} alt="Como chegar até a Igreja Batista Mosaico" />
        <div className="addr-row">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.4" /></svg>
          <div>
            <div className="addr-text">{VENUE.address}</div>
            <a className="maplink" href={VENUE.mapsUrl} target="_blank" rel="noopener">Ver rota no mapa
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
